import ClientPanelInvitationDialog from './ClientPanelInvitationDialog';
import { Box, IconButton, Spacer, Stack, Text } from '@chakra-ui/react';
import { FiCalendar, FiCopy, FiMail, FiPhone, FiTrash2 } from 'react-icons/fi';
import { formatPhoneNumber, getClientEmailList } from './clientTableUtils';
import { formatSlotSummary } from '@opanuj/shared/utils';
import { Link } from 'react-router-dom';
import { SlotReservationDialog } from '../slots/SlotReservationForm';
import { toaster } from '../../../components/ui/toaster';
import { useMemo } from 'react';

export type ClientPreviewPanelProps = {
	client: any;
	reservations?: any[];
	slots?: any[];
	isReservationOpen?: boolean;
	onOpenReservation?: () => void;
	onCloseReservation?: () => void;
	onProductDelete?: (slotId: string) => void;
};

const ClientPreviewPanel = ({
	client,
	reservations = [],
	slots = [],
	isReservationOpen = false,
	onCloseReservation,
	onProductDelete,
}: ClientPreviewPanelProps) => {
	const emailList = getClientEmailList(client);

	const billingInfo = [
		client?.billing?.name,
		client?.billing?.address,
		`${client?.billing?.postalCode ?? ''} ${client?.billing?.city ?? ''}`.trim(),
		client?.billing?.nip ? `NIP: ${client.billing.nip}` : null,
	].filter(Boolean);

	const copyToClipboard = async (value: string) => {
		if (!value) return;
		try {
			await navigator.clipboard.writeText(value);
			toaster.create({
				title: 'Skopiowano do schowka',
				description: value,
				type: 'success',
				duration: 2000,
			});
		} catch (error) {
			console.error('Clipboard copy failed', error);
			toaster.create({
				title: 'Nie udało się skopiować',
				type: 'error',
				duration: 2000,
			});
		}
	};

	const clientReservations = useMemo(() => {
		if (!client?.uid) {
			return [];
		}
		return reservations.filter((reservation) => reservation?.uid === client.uid);
	}, [client?.uid, reservations]);

	const slotSummaries = useMemo(() => {
		return clientReservations.flatMap((reservation: any) => {
			return (reservation?.lessons ?? [])
				.map((lesson: any) => {
					const slot = slots.find((slotItem: any) => slotItem.id === lesson.slot);
					if (!slot) return null;
					return {
						slotId: slot.id,
						summary: formatSlotSummary(slot),
					};
				})
				.filter(Boolean);
		});
	}, [clientReservations, slots]);
	const productSummaries = useMemo(() => {
		return Object.values(client?.products ?? {})
			.map((product: any) => {
				const slot = slots.find((slotItem: any) => slotItem.id === product.slotId);
				if (!slot) return null;
				return {
					slotId: slot.id,
					summary: formatSlotSummary(slot),
					studentName: product.student?.name,
				};
			})
			.filter(Boolean);
	}, [client?.products, slots]);

	return (
		<Stack gap={4} h="full">
			<Stack gap={2}>
				<ClientPanelInvitationDialog client={client} />
				<Box>
					<Text fontSize="xs" fontWeight="bold" color="gray.500" mb={0.5} letterSpacing="wide">Emaile kontaktowe</Text>
					<Stack gap={1} pl={2}>
						{emailList.map((email: string) => (
							<Box key={email} display="flex" alignItems="center" gap={2}>
								<Text flex={1}>{email}</Text>
								<Box display="flex" alignItems="center" gap={1}>
									<IconButton
										aria-label={`Wyślij mail do ${email}`}
										size="xs"
										variant="ghost"
										onClick={() => window.location.href = `mailto:${email}`}
									>
										<FiMail />
									</IconButton>
									<IconButton
										aria-label={`Kopiuj ${email}`}
										size="xs"
										variant="ghost"
										onClick={() => copyToClipboard(email)}
									>
										<FiCopy />
									</IconButton>
								</Box>
							</Box>
						))}
					</Stack>
				</Box>
				<Box>
					<Text fontSize="xs" fontWeight="bold" color="gray.500" mb={0.5} letterSpacing="wide">Telefon</Text>
					<Box display="flex" alignItems="center" gap={2} pl={2}>
						<Text flex={1}>{formatPhoneNumber(client?.phone)}</Text>
						<Box display="flex" alignItems="center" gap={1}>
							<IconButton
								aria-label="Zadzwoń"
								size="xs"
								variant="ghost"
								onClick={() => {
									if (client?.phone) {
										window.location.href = `tel:${client.phone.replace(/\D/g, '')}`;
									}
								}}
							>
								<FiPhone />
							</IconButton>
							<IconButton
								aria-label="Kopiuj numer telefonu"
								size="xs"
								variant="ghost"
								onClick={() => copyToClipboard(client?.phone ? client.phone.replace(/\D/g, '') : '')}
							>
								<FiCopy />
							</IconButton>
						</Box>
					</Box>
				</Box>
			</Stack>
			<Box>
				<Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
					<Text fontSize="xs" fontWeight="bold" color="gray.500" letterSpacing="wide">Dane do faktury</Text>
					{billingInfo.length > 0 && (
						<IconButton
							aria-label="Kopiuj dane do faktury"
							size="xs"
							variant="ghost"
							onClick={() => copyToClipboard(billingInfo.join('\n'))}
						>
							<FiCopy />
						</IconButton>
					)}
				</Box>
				<Stack gap={1} mt={1} pl={2}>
					{billingInfo.length ? billingInfo.map((line, index) => (
						<Text key={`${line}-${index}`}>{line}</Text>
					)) : <Text>Brak danych</Text>}
				</Stack>
			</Box>
			<Box>
				<Text fontSize="xs" fontWeight="bold" color="gray.500" letterSpacing="wide">Otwarte rezerwacje</Text>
				{slotSummaries.length ? (
					<Stack gap={2} mt={2} ml={2}>
						{slotSummaries.map((item: { slotId: string; summary: string }, index: number) => (
							<Box key={`${item.slotId}-${index}`} display="flex" alignItems="center" gap={2}>
								<Box as="span" color="gray.500" display="inline-flex" alignItems="center">
									<FiCalendar />
								</Box>
								<Link
									to={`/admin/terminy/${item.slotId}`}
									style={{
										color: '#718096',
										textDecoration: 'underline',
									}}
								>
									<Text as="span" color="gray.500" textDecoration="underline">{item.summary}</Text>
								</Link>
							</Box>
						))}
					</Stack>
				) : (
					<Text mt={2} pl={2}>Brak otwartych rezerwacji</Text>
				)}
			</Box>
			<Box>
				<Text fontSize="xs" fontWeight="bold" color="gray.500" letterSpacing="wide">Aktywne zajęcia</Text>
				{productSummaries.length ? (
					<Stack gap={2} mt={2} ml={2}>
						{productSummaries.filter(item => item !== null).map((item: { slotId: string; summary: string; studentName?: string }, index: number) => (
							<Box key={`${item.slotId}-${index}`} display="flex" alignItems="center" gap={2}>
								<Box as="span" color="gray.500" display="inline-flex" alignItems="center">
									<FiCalendar />
								</Box>
								<Link
									to={`/admin/terminy/${item.slotId}`}
									style={{
										color: '#718096',
										textDecoration: 'underline',
									}}
								>
									<Text as="span" color="gray.500" textDecoration="underline">
										{item.summary}{item.studentName ? ` - ${item.studentName}` : ''}
									</Text>
								</Link>
								{onProductDelete && (
									<IconButton
										aria-label={`Usuń zajęcia ${item.summary}${item.studentName ? ` - ${item.studentName}` : ''}`}
										size="xs"
										variant="ghost"
										colorPalette="red"
										onClick={() => onProductDelete(item.slotId)}
									>
										<FiTrash2 />
									</IconButton>
								)}
							</Box>
						))}
					</Stack>
				) : (
					<Text mt={2} pl={2}>Brak aktywnych produktów</Text>
				)}
			</Box>
			{isReservationOpen && client && onCloseReservation && (
				<SlotReservationDialog
					open={isReservationOpen}
					slots={[]}
					availableSlots={slots}
					initialClient={client}
					onClose={onCloseReservation}
				/>
			)}
			
			<Spacer />
			<Box>
				<Stack gap={1} mt={2}>
					{client?.id && (
						<Box display="flex" alignItems="center" gap={2}>
							<Text fontSize="xs" color="gray.500" flex={1}>
								<strong style={{ fontWeight: 600 }}>ID klienta:</strong> {client.id}
							</Text>
							<IconButton aria-label="Kopiuj ID klienta" size="2xs" variant="ghost" onClick={() => copyToClipboard(client.id)}>
								<FiCopy />
							</IconButton>
						</Box>
					)}
					{client?.uid && (
						<Box display="flex" alignItems="center" gap={2}>
							<Text fontSize="xs" color="gray.500" flex={1}>
								<strong style={{ fontWeight: 600 }}>ID konta:</strong> {client.uid}
							</Text>
							<IconButton aria-label="Kopiuj ID konta" size="2xs" variant="ghost" onClick={() => copyToClipboard(client.uid)}>
								<FiCopy />
							</IconButton>
						</Box>
					)}
					{client?.ifirmaId && (
						<Box display="flex" alignItems="center" gap={2}>
							<Text fontSize="xs" color="gray.500" flex={1}>
								<strong style={{ fontWeight: 600 }}>IFirma ID:</strong> {client.ifirmaId}
							</Text>
							<IconButton aria-label="Kopiuj IFirma ID" size="2xs" variant="ghost" onClick={() => copyToClipboard(client.ifirmaId)}>
								<FiCopy />
							</IconButton>
						</Box>
					)}
				</Stack>
			</Box>
		</Stack>
	);
};

export default ClientPreviewPanel;
